import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import authApi from '../../auth.api';
import {
  CHECK_LINKED_ACCOUNTS,
  GENERATE_SERIES,
  GET_SERIES,
  GET_THEMES,
  GET_USER_TIKTOK_INFO,
  GET_VOICES,
  GOOGLE_AUTH_API,
  PUBLISH_VIDEO,
  SERIES,
  TIKTOK_AUTH_API,
} from '../../route';
import { toast } from 'react-toastify';
import axios from 'axios';
import SideBar from '../../components/SideBar';
import { motion } from 'framer-motion';
import CreateSeriesCard from '../../components/series/create-series/CreateSeriesCard';
import PreviewSection from '../../components/series/create-series/PreviewSection';
import CreateSeriesForm from '../../components/series/create-series/CreateSeriesForm';
import CreateSeriesQuickTipCard from '../../components/series/create-series/CreateSeriesQuickTipCard';
import { Eye, Mic, Video } from 'lucide-react';
import LinkAccountModal from '../../components/series/create-series/LinkAccountModal';
import { SiYoutube } from 'react-icons/si';

// Constants
export const languages = [
  'English', 'Chinese', 'Korean', 'Dutch', 'Turkish', 'Swedish', 'Indonesian',
  'Filipino', 'Japanese', 'Ukrainian', 'Greek', 'Czech', 'Finnish', 'Romanian',
  'Russian', 'Danish', 'Bulgarian', 'Malay', 'Slovak', 'Croatian', 'Classic',
  'Arabic', 'Tamil', 'Polish', 'German', 'Spanish', 'French', 'Italian',
  'Hindi', 'Portuguese', 'Hungarian', 'Vietnamese', 'Norwegian',
];

const PLATFORMS = {
  YOUTUBE: 'YouTube Shorts',
  TIKTOK: 'TikTok',
};

const DEFAULT_PUBLISH_TIME = { hour: 16, minute: 0 };

const INITIAL_SERIES_DATA = {
  artStyle: '',
  platforms: [],
  contentType: '',
  customTopic: '',
  voice: '',
  language: '',
  duration: '',
  script: '',
  privacyStatus: '',
  allowComments: false,
  allowDuet: false,
  allowStitch: false,
  title: '',
};

const INITIAL_TIKTOK_METADATA = {
  title: '',
  privacyStatus: '',
  allowComments: false,
  allowDuet: false,
  allowStitch: false,
  commercialContent: false,
  yourBrand: false,
  brandedContent: false,
  tags: [],
};

const INITIAL_YOUTUBE_METADATA = {
  title: '',
  tags: [],
  description: '',
  privacyStatus: '',
};

const CreateSeries = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Extract series ID from URL
  const seriesIdFromUrl = pathname.match(/\/create-series\/([^/]+)/)?.[1] ||
      pathname.match(/\/series\/preview\/([^/]+)/)?.[1];

  // State management
  const [linkedAccounts, setLinkedAccounts] = useState({
    [PLATFORMS.YOUTUBE]: false,
    [PLATFORMS.TIKTOK]: false,
  });
  const [showLinkAccountModal, setShowLinkAccountModal] = useState(false);
  const [loggedInUserEmail, setLoggedInUserEmail] = useState('');
  const [seriesData, setSeriesData] = useState(INITIAL_SERIES_DATA);
  const [showingPreview, setShowingPreview] = useState(false);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [publishTime, setPublishTime] = useState(DEFAULT_PUBLISH_TIME);
  const [previewGenerated, setPreviewGenerated] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewVideo, setPreviewVideo] = useState('');
  const [videoId, setVideoId] = useState('');
  const [seriesId, setSeriesId] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [isEditingScript, setIsEditingScript] = useState(false);
  const [editedScript, setEditedScript] = useState(seriesData.script);
  const [isLoading, setIsLoading] = useState(true);
  const [voices, setVoices] = useState([]);
  const [themes, setThemes] = useState([]);
  const [isLinkingYoutube, setIsLinkingYouTube] = useState(false);
  const [isLinkingTikTok, setIsLinkingTikTok] = useState(false);
  const [showHoverNotification, setShowHoverNotification] = useState(true);
  const [showConnectionView, setShowConnectionView] = useState(true);
  const [tiktokPostMetadata, setTiktokPostMetadata] = useState(INITIAL_TIKTOK_METADATA);
  const [youtubePostMetadata, setYoutubePostMetadata] = useState(INITIAL_YOUTUBE_METADATA);

  // Helper functions
  const showToastError = (message) => {
    toast.error(message);
  };

  const showToastSuccess = (message) => {
    toast.success(message);
  };

  // Check linked accounts for platforms
  const checkLinkedAccounts = async (platforms = seriesData.platforms) => {
    const response = await authApi.post(CHECK_LINKED_ACCOUNTS, { platforms });
    if (response.status) {
      setLinkedAccounts(response.data.linkedPlatforms);
      setLoggedInUserEmail(response.data.email);
      return response.data.linkedPlatforms;
    }
    showToastError('Failed to check linked accounts. Please try again.');
  };

  // Load initial data on component mount
  const loadInitialData = async () => {
    try {
      await Promise.all([
        onLoadCheckLinkedAccounts(),
        fetchVoices(),
        fetchThemes(),
      ]);
    } catch (error) {
      showToastError('Failed to load initial data. Please try again.');
    }
  };

  // Fetch voices from API
  const fetchVoices = async () => {
    const { data } = await authApi.get(GET_VOICES);
    setVoices(data.voices);
  };

  // Fetch themes from API
  const fetchThemes = async () => {
    try {
      const { data } = await authApi.get(GET_THEMES);
      setThemes(data.themes);
    } catch (error) {
      showToastError('Failed to load content types. Please try again.');
    }
  };

  // Handle OAuth authentication for platforms
  const handleOAuthSignIn = async (platform, apiUrl, setLoadingState) => {
    setLoadingState(true);
    try {
      const url = `${apiUrl}?email=${loggedInUserEmail}&seriesId=${seriesId}`;
      const response = platform === PLATFORMS.YOUTUBE
          ? await authApi.get(url)
          : await axios.get(url);

      const authUrl = platform === PLATFORMS.YOUTUBE
          ? response.data.url
          : response.data.data.url;

      if ((platform === PLATFORMS.YOUTUBE && response.status) ||
          (platform === PLATFORMS.TIKTOK && response.data.status)) {
        window.open(authUrl, '_blank', 'noopener,noreferrer');
      } else {
        const errorMessage = platform === PLATFORMS.YOUTUBE
            ? response.data.message || 'Registration with YouTube failed'
            : response.data.message || 'Registration with TikTok failed';
        showToastError(errorMessage);
      }
    } catch (err) {
      showToastError(`Failed to authenticate with ${platform}`);
    } finally {
      setLoadingState(false);
    }
  };

  const handleGoogleSignIn = () => {
    handleOAuthSignIn(PLATFORMS.YOUTUBE, GOOGLE_AUTH_API, setIsLinkingYouTube);
  };

  const handleTikTokSignIn = () => {
    handleOAuthSignIn(PLATFORMS.TIKTOK, TIKTOK_AUTH_API, setIsLinkingTikTok);
  };

  // Generic handler for form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSeriesData((prev) => ({ ...prev, [name]: value }));
  };

  // Toggle platform selection
  const handlePlatformToggle = (platform) => {
    setSeriesData((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
          ? prev.platforms.filter((p) => p !== platform)
          : [...prev.platforms, platform],
    }));
  };

  // Handle option selection for various fields
  const handleOptionSelect = (field, value) => {
    setSeriesData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle publish time changes
  const handlePublishTimeChange = (e) => {
    const [hour, minute] = e.target.value.split(':');
    setPublishTime({ hour: parseInt(hour), minute: parseInt(minute) });
  };

  // Update metadata when series data changes
  const updateMetadataFromSeries = (updatedData) => {
    setTiktokPostMetadata((prev) => ({
      ...prev,
      title: updatedData.title,
      tags: updatedData.tags,
    }));
    setYoutubePostMetadata((prev) => ({
      ...prev,
      title: updatedData.title,
      tags: updatedData.tags,
      description: updatedData.description,
    }));
  };

  // Generic metadata change handler
  const handleMetadataChange = (setter, seriesField = null) => (e) => {
    const { name, value, type, checked } = e.target;
    setter((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (seriesField && name === seriesField) {
      setSeriesData((prev) => ({ ...prev, [seriesField]: value }));
    }
  };

  const handleTiktokMetadataChange = handleMetadataChange(setTiktokPostMetadata, 'title');
  const handleYoutubeMetadataChange = handleMetadataChange(setYoutubePostMetadata, 'description');

  // Handle title changes for both platforms
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTiktokPostMetadata((prev) => ({ ...prev, title: newTitle }));
    setSeriesData((prev) => ({ ...prev, title: newTitle }));
  };

  const handleYoutubeTitleChange = (e) => {
    const newTitle = e.target.value;
    setYoutubePostMetadata((prev) => ({ ...prev, title: newTitle }));
    setSeriesData((prev) => ({ ...prev, title: newTitle }));
  };

  // Handle privacy status changes
  const handlePrivacyStatusChange = (e) => {
    const { value } = e.target;
    setTiktokPostMetadata((prev) => ({
      ...prev,
      privacyStatus: value,
      brandedContent: value === 'Self Only' ? false : prev.brandedContent,
    }));
  };

  const handleYoutubePrivacyStatusChange = (e) => {
    const { value } = e.target;
    setYoutubePostMetadata((prev) => ({ ...prev, privacyStatus: value }));
  };

  // Handle commercial content changes
  const handleCommercialContentChange = (e) => {
    const { checked } = e.target;
    setTiktokPostMetadata((prev) => ({
      ...prev,
      commercialContent: checked,
      yourBrand: checked ? prev.yourBrand : false,
      brandedContent: checked ? prev.brandedContent : false,
      privacyStatus: checked && prev.brandedContent ? 'public' : prev.privacyStatus,
    }));
  };

  // Validation functions
  const validateForm = () => {
    if (seriesData.platforms.length === 0) {
      showToastError('Please select at least one platform');
      return false;
    }
    if (!seriesData.contentType) {
      showToastError('Please select a content type');
      return false;
    }
    if (!seriesData.voice) {
      showToastError('Please select voice');
      return false;
    }
    if (seriesData.contentType === 'Custom Topic' && !seriesData.customTopic) {
      showToastError('Please enter a custom topic');
      return false;
    }
    return true;
  };

  const validateTiktokPost = () => {
    if (seriesData.platforms.includes(PLATFORMS.TIKTOK) && !linkedAccounts[PLATFORMS.TIKTOK]) {
      setShowLinkAccountModal(true);
      return false;
    }
    if (!tiktokPostMetadata.privacyStatus) {
      showToastError('Please select a privacy status for your TikTok post');
      return false;
    }
    if (tiktokPostMetadata.commercialContent &&
        !tiktokPostMetadata.yourBrand &&
        !tiktokPostMetadata.brandedContent) {
      showToastError('Please select at least one commercial content option');
      return false;
    }
    return true;
  };

  const validateYoutubePost = () => {
    if (seriesData.platforms.includes(PLATFORMS.YOUTUBE) &&
        !linkedAccounts[PLATFORMS.YOUTUBE]) {
      setShowLinkAccountModal(true);
      return false;
    }
    if (!youtubePostMetadata.privacyStatus) {
      showToastError('Please select a privacy status for your youtube post');
      return false;
    }
    return true;
  };

  // Fetch creator info for TikTok
  const fetchCreatorInfo = async () => {
    try {
      const { data } = await authApi.get(GET_USER_TIKTOK_INFO);
      setCreatorInfo(data.tikTokInfo);
      setSeriesData((prevData) => ({
        ...prevData,
        nickname: data.tikTokInfo.tikTokNickname,
      }));
    } catch (error) {
      console.error('Error fetching creator info:', error);
      showToastError('Failed to retrieve creator info. Please try again.');
    }
  };

  // Handle script updates
  const handleScriptUpdate = async () => {
    try {
      setSeriesData((prev) => ({ ...prev, script: editedScript }));
      setIsEditingScript(false);
      showToastSuccess('Script updated successfully');
    } catch (error) {
      console.error('Error updating script:', error);
      showToastError('Failed to update script. Please try again.');
    }
  };

  // Handle preview generation
  const handlePreviewClick = async () => {
    if (!validateForm()) return;

    setIsGeneratingPreview(true);
    const seriesRequestData = {
      theme: seriesData.contentType,
      duration: seriesData.duration || '1',
      platforms: seriesData.platforms.map((one) => one),
      voice: seriesData.voice.id,
      language: seriesData.language || 'English',
      publishTime: publishTime,
      artStyle: seriesData.artStyle || ''
    };

    if (seriesData.customTopic.length > 0) {
      seriesRequestData.customTopic = seriesData.customTopic;
    }

    try {
      const result = await authApi.post(GENERATE_SERIES, seriesRequestData);

      if (result.status) {
        setSeriesData((prevData) => ({
          ...prevData,
          ...result.data.series,
          script: result.data.series.script || prevData.script,
          description: result.data.series.description || '',
          tags: result.data.series.tags || '',
        }));
        setPreviewVideo(result.data.uploadUrl);
        setVideoId(result.data.series._id);
        setSeriesId(result.data.seriesId);
        setPreviewGenerated(true);
        setShowingPreview(true);
        showToastSuccess('Preview generated successfully!');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Handle publish functionality
  const handlePublish = async () => {
    if (seriesData.platforms.includes(PLATFORMS.YOUTUBE) &&
        !linkedAccounts[PLATFORMS.YOUTUBE]) {
      setShowLinkAccountModal(true);
      return;
    }

    if (seriesData.platforms.includes(PLATFORMS.TIKTOK) && !validateTiktokPost()) return;
    if (seriesData.platforms.includes(PLATFORMS.YOUTUBE) && !validateYoutubePost()) return;

    setIsPublishing(true);
    try {
      const response = await authApi.post(PUBLISH_VIDEO, {
        video: videoId,
        ...(seriesData.platforms.includes(PLATFORMS.TIKTOK) && {
          tiktokMetadata: tiktokPostMetadata,
        }),
        ...(seriesData.platforms.includes(PLATFORMS.YOUTUBE) && {
          youtubeMetadata: youtubePostMetadata,
        }),
      });

      if (response.status) {
        let successMessage = `Published to ${seriesData.platforms.join(' and ')}`;
        if (seriesData.platforms.includes(PLATFORMS.TIKTOK)) {
          successMessage += '. It may take a few minutes for the content to process and be visible on your profile.';
        }
        showToastSuccess(successMessage);
        navigate(SERIES);
      }
    } catch (error) {
      showToastError('Failed to publish series. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  // Fetch existing series data
  const fetchSeries = async () => {
    try {
      const { data } = await authApi.get(`${GET_SERIES}/${seriesIdFromUrl}`);
      const series = data.series;
      const video = series.videos[0];

      setSeriesData({
        ...series,
        platforms: series.platforms,
        tags: video.tags,
        language: series.language,
        voice: series.voice,
        theme: series.theme,
        publishTime: series.publishTime
      });

      setPreviewVideo(video.uploadUrl);
      setVideoId(video._id);
      setSeriesId(series._id);
      setPreviewGenerated(true);
      setShowingPreview(true);

      setTiktokPostMetadata((prev) => ({
        ...prev,
        title: video.title,
        tags: video.tags,
      }));

      setYoutubePostMetadata((prev) => ({
        ...prev,
        title: video.title,
        tags: video.tags,
        description: video.description,
      }));
    } catch (error) {
      showToastError('Failed to load series. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Check linked accounts on component load
  const onLoadCheckLinkedAccounts = async () => {
    setIsLoading(true);
    const response = await authApi.post(CHECK_LINKED_ACCOUNTS, {
      platforms: [PLATFORMS.TIKTOK, PLATFORMS.YOUTUBE],
    });

    if (response.status) {
      setLinkedAccounts(response.data.linkedPlatforms);
      setLoggedInUserEmail(response.data.email);
      setShowConnectionView(
          !response.data.linkedPlatforms[PLATFORMS.YOUTUBE] &&
          !response.data.linkedPlatforms[PLATFORMS.TIKTOK]
      );
    } else {
      showToastError('Failed to check linked accounts. Please try again.');
    }
    setIsLoading(false);
  };

  // Get TikTok declaration text
  const getDeclaration = () => {
    if (!tiktokPostMetadata.commercialContent) {
      return "By posting, you agree to TikTok's Music Usage Confirmation.";
    }
    if (tiktokPostMetadata.brandedContent) {
      return "By posting, you agree to TikTok's Branded Content Policy and Music Usage Confirmation.";
    }
    return "By posting, you agree to TikTok's Music Usage Confirmation.";
  };

  // Check if publish button should be disabled
  const isPublishDisabled =
      seriesData.platforms.includes(PLATFORMS.TIKTOK) &&
      tiktokPostMetadata.commercialContent &&
      !tiktokPostMetadata.yourBrand &&
      !tiktokPostMetadata.brandedContent;

  // Connect platform card component
  const ConnectPlatformCard = ({ platform, icon, isConnected, onConnect }) => (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          {icon}
          {isConnected ? (
              <span className="text-green-500 text-sm font-medium">Connected</span>
          ) : (
              <button
                  onClick={onConnect}
                  className="text-indigo-600 bg-indigo-50 px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-100"
              >
                Connect
              </button>
          )}
        </div>
        <h3 className="font-semibold text-indigo-600">{platform}</h3>
        <p className="text-sm text-gray-500 mt-2">
          {isConnected
              ? `Your ${platform} account is ready to publish content`
              : `Connect your ${platform} account to publish videos`}
        </p>
      </div>
  );

  // Effects
  useEffect(() => {
    if (seriesIdFromUrl) {
      fetchSeries();
    }
  }, [seriesIdFromUrl]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromAuth = params.get('fromAuth');
    if (fromAuth) {
      checkLinkedAccounts();
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (seriesData.title) {
      updateMetadataFromSeries(seriesData);
    }
  }, [seriesData.description]);

  useEffect(() => {
    checkLinkedAccounts();
    if (seriesData.platforms.includes(PLATFORMS.TIKTOK) &&
        linkedAccounts[PLATFORMS.TIKTOK] &&
        previewGenerated) {
      fetchCreatorInfo();
    }
  }, [seriesData.platforms, linkedAccounts[PLATFORMS.TIKTOK], previewGenerated]);

  return (
      <div className='flex h-screen bg-gray-50'>
        <SideBar />
        <div className='flex-1 overflow-auto p-8'>
          <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
          >
            <h2 className='text-4xl font-bold mb-8 text-gray-800 tracking-tight'>
              Create New Series
            </h2>
            {isLoading ? (
                <>
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
                  </div>
                  <CreateSeriesCard className='mt-8'>
                    <h3 className='text-2xl font-semibold mb-6 text-gray-800'>
                      Quick Tips
                    </h3>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                      <CreateSeriesQuickTipCard
                          icon={Mic}
                          title='Voice Consistency'
                          description='Maintain a consistent voice across your series'
                      />
                      <CreateSeriesQuickTipCard
                          icon={Eye}
                          title='Preview Your Series'
                          description='Always review your series details before publishing'
                      />
                    </div>
                  </CreateSeriesCard>
                </>
            ) : showConnectionView ? (
                <>
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <ConnectPlatformCard
                        platform={PLATFORMS.YOUTUBE}
                        icon={<SiYoutube className="w-8 h-8" />}
                        isConnected={linkedAccounts[PLATFORMS.YOUTUBE]}
                        onConnect={handleGoogleSignIn}
                    />
                    <ConnectPlatformCard
                        platform={PLATFORMS.TIKTOK}
                        icon={<Video className="w-8 h-8" />}
                        isConnected={linkedAccounts[PLATFORMS.TIKTOK]}
                        onConnect={handleTikTokSignIn}
                    />
                    <button
                        onClick={() => setShowConnectionView(false)}
                        className="col-span-2 text-gray-600 hover:text-gray-800"
                    >
                      Skip for now
                    </button>
                  </div>
                  <CreateSeriesCard className='mt-8'>
                    <h3 className='text-2xl font-semibold mb-6 text-gray-800'>
                      Quick Tips
                    </h3>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                      <CreateSeriesQuickTipCard
                          icon={Mic}
                          title='Voice Consistency'
                          description='Maintain a consistent voice across your series'
                      />
                      <CreateSeriesQuickTipCard
                          icon={Eye}
                          title='Preview Your Series'
                          description='Always review your series details before publishing'
                      />
                    </div>
                  </CreateSeriesCard>
                </>
            ) : (
                <>
                  <CreateSeriesCard className='mb-8'>
                    {showingPreview ? (
                        <PreviewSection
                            isPublishing={isPublishing}
                            handleTikTokSignIn={handleTikTokSignIn}
                            isLinkingTikTok={isLinkingTikTok}
                            handleGoogleSignIn={handleGoogleSignIn}
                            previewVideo={previewVideo}
                            setShowingPreview={setShowingPreview}
                            isPublishDisabled={isPublishDisabled}
                            handlePublish={handlePublish}
                            linkedAccounts={linkedAccounts}
                            isLinkingYoutube={isLinkingYoutube}
                            youtubePostMetadata={youtubePostMetadata}
                            handleYoutubeTitleChange={handleYoutubeTitleChange}
                            handleYoutubePrivacyStatusChange={handleYoutubePrivacyStatusChange}
                            handleYoutubeMetadataChange={handleYoutubeMetadataChange}
                            getDeclaration={getDeclaration}
                            handlePrivacyStatusChange={handlePrivacyStatusChange}
                            handleTitleChange={handleTitleChange}
                            creatorInfo={creatorInfo}
                            seriesData={seriesData}
                            setShowHoverNotification={setShowHoverNotification}
                            handleTiktokMetadataChange={handleTiktokMetadataChange}
                            showHoverNotification={showHoverNotification}
                            tiktokPostMetadata={tiktokPostMetadata}
                            handleCommercialContentChange={handleCommercialContentChange}
                        />
                    ) : (
                        <CreateSeriesForm
                            languages={languages}
                            voices={voices}
                            handleChange={handleChange}
                            isGeneratingPreview={isGeneratingPreview}
                            handlePreviewClick={handlePreviewClick}
                            themes={themes}
                            previewGenerated={previewGenerated}
                            handleOptionSelect={handleOptionSelect}
                            setShowingPreview={setShowingPreview}
                            handlePublishTimeChange={handlePublishTimeChange}
                            handlePlatformToggle={handlePlatformToggle}
                            seriesData={seriesData}
                            publishTime={publishTime}
                        />
                    )}
                  </CreateSeriesCard>
                  <CreateSeriesCard className='mt-8'>
                    <h3 className='text-2xl font-semibold mb-6 text-gray-800'>
                      Quick Tips
                    </h3>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                      <CreateSeriesQuickTipCard
                          icon={Mic}
                          title='Voice Consistency'
                          description='Maintain a consistent voice across your series'
                      />
                      <CreateSeriesQuickTipCard
                          icon={Eye}
                          title='Preview Your Series'
                          description='Always review your series details before publishing'
                      />
                    </div>
                  </CreateSeriesCard>
                </>
            )}
          </motion.div>
        </div>
        {showLinkAccountModal && (
            <LinkAccountModal
                onSkip={() => setShowLinkAccountModal(false)}
                seriesData={seriesData}
                linkedAccounts={linkedAccounts}
                setLinkedAccounts={setLinkedAccounts}
                setShowLinkAccountModal={setShowLinkAccountModal}
                handlePublish={handlePublish}
                handleTikTokSignIn={handleTikTokSignIn}
                handleGoogleSignIn={handleGoogleSignIn}
                platforms={seriesData.platforms}
            />
        )}
      </div>
  );
};

export default CreateSeries;